import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import Grid, { GridItem } from '../../components/layout/Grid';
import CampoTexto from '../../components/Formulario/CampoTexto';
import CampoMemo from '../../components/Formulario/CampoMemo';
import Botao from '../../components/Botao';
import { mdiContentSave, mdiLoading, mdiMagnify, mdiMapMarker, mdiTrashCan, mdiUpload } from '@mdi/js';
import { OrganizacaoDto } from '@tera/shared/src/dto/OrganizacaoDto';
import CampoMascara from '@/components/Formulario/CampoMascara';
import CampoMultiTexto from '@/components/Formulario/CampoMultiTexto';
import CampoSeletor, { CampoSeletorOption } from '@/components/Formulario/CampoSeletor';

const TIMEZONE_OPTIONS: CampoSeletorOption[] = [
    // ── Brasil ───────────────────────────────────────────────────
    { id: 'America/Noronha', label: 'America/Noronha — UTC-2 (Fernando de Noronha)' },
    { id: 'America/Fortaleza', label: 'America/Fortaleza — UTC-3 (Fortaleza, Recife, Maceió, Natal, Belém)' },
    { id: 'America/Sao_Paulo', label: 'America/Sao_Paulo — UTC-3 (Brasília, São Paulo, Rio de Janeiro, Sul/Sudeste)' },
    { id: 'America/Bahia', label: 'America/Bahia — UTC-3 (Salvador, Bahia)' },
    { id: 'America/Manaus', label: 'America/Manaus — UTC-4 (Manaus, Cuiabá, Porto Velho, Boa Vista)' },
    { id: 'America/Rio_Branco', label: 'America/Rio_Branco — UTC-5 (Rio Branco, Acre)' },
    // ── América do Sul ──────────────────────────────────────────
    { id: 'America/Argentina/Buenos_Aires', label: 'America/Argentina/Buenos_Aires — UTC-3 (Buenos Aires, Argentina)' },
    { id: 'America/Santiago', label: 'America/Santiago — UTC-4/-3 (Santiago, Chile)' },
    { id: 'America/Lima', label: 'America/Lima — UTC-5 (Lima, Peru)' },
    { id: 'America/Bogota', label: 'America/Bogota — UTC-5 (Bogotá, Colômbia)' },
    { id: 'America/Caracas', label: 'America/Caracas — UTC-4 (Caracas, Venezuela)' },
    // ── América do Norte ───────────────────────────────────────
    { id: 'America/Halifax', label: 'America/Halifax — UTC-4/-3 (Halifax, Atlântico Canadense)' },
    { id: 'America/New_York', label: 'America/New_York — UTC-5/-4 (Nova Iorque, Miami, Toronto)' },
    { id: 'America/Chicago', label: 'America/Chicago — UTC-6/-5 (Chicago, Houston, México)' },
    { id: 'America/Denver', label: 'America/Denver — UTC-7/-6 (Denver, Phoenix)' },
    { id: 'America/Los_Angeles', label: 'America/Los_Angeles — UTC-8/-7 (Los Angeles, Seattle, Vancouver)' },
    { id: 'America/Anchorage', label: 'America/Anchorage — UTC-9/-8 (Anchorage, Alasca)' },
    { id: 'Pacific/Honolulu', label: 'Pacific/Honolulu — UTC-10 (Honolulu, Havaí)' },
    { id: 'Pacific/Midway', label: 'Pacific/Midway — UTC-11 (Ilhas Midway)' },
    { id: 'Etc/GMT+12', label: 'Etc/GMT+12 — UTC-12 (Linha Internacional de Data — Oeste)' },
    // ── Europa ─────────────────────────────────────────────────────
    { id: 'Atlantic/Azores', label: 'Atlantic/Azores — UTC-1/+0 (Açores)' },
    { id: 'UTC', label: 'UTC — UTC+0' },
    { id: 'Europe/Lisbon', label: 'Europe/Lisbon — UTC+0/+1 (Lisboa, Portugal)' },
    { id: 'Europe/London', label: 'Europe/London — UTC+0/+1 (Londres, Dublin)' },
    { id: 'Europe/Paris', label: 'Europe/Paris — UTC+1/+2 (Paris, Berlim, Roma, Madrid)' },
    { id: 'Europe/Helsinki', label: 'Europe/Helsinki — UTC+2/+3 (Helsinki, Atenas, Cairo, Israel)' },
    { id: 'Europe/Moscow', label: 'Europe/Moscow — UTC+3 (Moscou, Turquia, Afeganistão)' },
    // ── África ───────────────────────────────────────────────────────
    { id: 'Africa/Abidjan', label: 'Africa/Abidjan — UTC+0 (África Ocidental)' },
    { id: 'Africa/Johannesburg', label: 'Africa/Johannesburg — UTC+2 (África do Sul, Zimbabwe)' },
    { id: 'Africa/Nairobi', label: 'Africa/Nairobi — UTC+3 (Quênia, Etifópia, Tanânia)' },
    // ── Ásia / Oriente Médio ─────────────────────────────────────────
    { id: 'Asia/Dubai', label: 'Asia/Dubai — UTC+4 (Dubai, Abu Dhabi, Baku)' },
    { id: 'Asia/Karachi', label: 'Asia/Karachi — UTC+5 (Paqistão, Uzbequístão)' },
    { id: 'Asia/Kolkata', label: 'Asia/Kolkata — UTC+5:30 (Índia, Sri Lanka)' },
    { id: 'Asia/Kathmandu', label: 'Asia/Kathmandu — UTC+5:45 (Nepal)' },
    { id: 'Asia/Dhaka', label: 'Asia/Dhaka — UTC+6 (Bangladesh, Butão)' },
    { id: 'Asia/Yangon', label: 'Asia/Yangon — UTC+6:30 (Mianmar)' },
    { id: 'Asia/Bangkok', label: 'Asia/Bangkok — UTC+7 (Tailândia, Vietnã, Camboja)' },
    { id: 'Asia/Shanghai', label: 'Asia/Shanghai — UTC+8 (China, Singapura, Perth)' },
    { id: 'Asia/Tokyo', label: 'Asia/Tokyo — UTC+9 (Japão, Coréia do Sul)' },
    { id: 'Australia/Adelaide', label: 'Australia/Adelaide — UTC+9:30/+10:30 (Adelaide, Darwin)' },
    { id: 'Australia/Sydney', label: 'Australia/Sydney — UTC+10/+11 (Sydney, Melbourne, Brisbane)' },
    { id: 'Pacific/Noumea', label: 'Pacific/Noumea — UTC+11 (Nova Caledônia, Océania central)' },
    { id: 'Pacific/Auckland', label: 'Pacific/Auckland — UTC+12/+13 (Nova Zelândia)' },
    { id: 'Pacific/Tongatapu', label: 'Pacific/Tongatapu — UTC+13 (Tonga)' },
    { id: 'Pacific/Kiritimati', label: 'Pacific/Kiritimati — UTC+14 (Kiribati — Linha Internacional de Data)' },
];

const SecaoLogo = () => {
    // A existência do logo é inferida do carregamento da própria imagem, evitando
    // um endpoint só para checar isso. `versao` força o refresh após upload/remoção.
    const [temLogo, setTemLogo] = useState(false);
    const [versao, setVersao] = useState(() => Date.now());
    const inputRef = React.useRef<HTMLInputElement>(null);

    const avisar = (msg: string, type: 'success' | 'error') =>
        toast(msg, { toastId: 'org-logo', position: 'top-center', type, autoClose: 5000 });

    const enviarMutation = useMutation(
        async (arquivo: File) => {
            const formData = new FormData();
            formData.append('logo', arquivo);
            return await axios.post('/api/organizacao/logo', formData, { withCredentials: true });
        },
        {
            onSuccess: () => {
                setVersao(Date.now());
                avisar('Logo atualizado. Recarregue a página para vê-lo na barra lateral.', 'success');
            },
            onError: (error: any) => {
                console.error(error);
                avisar(error.response?.data?.msg || 'Erro ao enviar o logo', 'error');
            },
        }
    );

    const removerMutation = useMutation(
        async () => await axios.delete('/api/organizacao/logo', { withCredentials: true }),
        {
            onSuccess: () => {
                setVersao(Date.now());
                avisar('Logo removido. Recarregue a página para vê-lo na barra lateral.', 'success');
            },
            onError: (error: any) => {
                console.error(error);
                avisar(error.response?.data?.msg || 'Erro ao remover o logo', 'error');
            },
        }
    );

    const ocupado = enviarMutation.isLoading || removerMutation.isLoading;

    return (
        <VBox gap="8px">
            <label style={{ fontWeight: 'bold' }}>Logo</label>
            <HBox gap="12px" style={{ alignItems: 'center' }}>
                <div
                    style={{
                        width: '96px',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid var(--tc-color-gray-300)',
                        borderRadius: '4px',
                        background: 'var(--tc-color-brand-secondary)',
                    }}
                >
                    <img
                        src={`/api/organizacao/logo?v=${versao}`}
                        alt="Logo da organização"
                        onLoad={() => setTemLogo(true)}
                        onError={() => setTemLogo(false)}
                        style={{
                            maxHeight: '40px',
                            maxWidth: '88px',
                            objectFit: 'contain',
                            display: temLogo ? 'block' : 'none',
                        }}
                    />
                    {!temLogo && (
                        <small style={{ color: 'var(--tc-color-gray-100)' }}>sem logo</small>
                    )}
                </div>

                <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: 'none' }}
                    onChange={e => {
                        const arquivo = e.target.files?.[0];
                        e.target.value = '';
                        if (arquivo) enviarMutation.mutate(arquivo);
                    }}
                />
                <Botao
                    variant="outline-primary"
                    icon={enviarMutation.isLoading ? mdiLoading : mdiUpload}
                    onClick={() => inputRef.current?.click()}
                    disabled={ocupado}
                >
                    {enviarMutation.isLoading ? 'Enviando...' : 'Enviar logo'}
                </Botao>
                {temLogo && (
                    <Botao
                        variant="outline-danger"
                        icon={removerMutation.isLoading ? mdiLoading : mdiTrashCan}
                        onClick={() => removerMutation.mutate()}
                        disabled={ocupado}
                    >
                        Remover
                    </Botao>
                )}
            </HBox>
            <small style={{ color: 'var(--tc-color-gray-600)' }}>
                Ao enviar um logo, ele e o nome fantasia passam a identificar o sistema na barra
                lateral. Sem logo, a marca Terachat é mantida. PNG, JPEG ou WEBP; a imagem é
                redimensionada para 128px de altura.
            </small>
        </VBox>
    );
};

const SecaoOrganizacao = () => {
    const [organizacao, setOrganizacao] = useState<OrganizacaoDto>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [isBuscandoCNPJ, setIsBuscandoCNPJ] = useState(false);
    const [isBuscandoCEP, setIsBuscandoCEP] = useState(false);

    const queryClient = useQueryClient();

    // Query para buscar dados da organização
    const { isLoading } = useQuery(
        'organizacao/detalhes',
        async () => {
            const response = await axios.get('/api/organizacao/detalhes', {
                withCredentials: true,
            });
            return response.data.result;
        },
        {
            onSuccess: (data) => {
                setOrganizacao(data || {});
            },
            onError: () => {
                toast('Erro ao carregar dados da organização', {
                    toastId: 'org-error',
                    position: 'top-center',
                    type: 'error',
                    autoClose: 5000,
                });
            },
            refetchOnWindowFocus: false,
        }
    );

    // Mutation para salvar dados
    const saveMutation = useMutation(
        async (data: OrganizacaoDto) => {
            return await axios.put('/api/organizacao/detalhes', data, {
                withCredentials: true,
            });
        },
        {
            onSuccess: () => {
                toast('Dados da organização salvos com sucesso', {
                    toastId: 'org-success',
                    position: 'top-center',
                    type: 'success',
                    autoClose: 5000,
                });
                queryClient.invalidateQueries('organizacao/detalhes');
                setFieldErrors({});
            },
            onError: (error: any) => {
                console.error(error);
                const fieldErrors = error.response?.data?.fieldErrors || {};
                const message = error.response?.data?.msg || 'Erro ao salvar dados da organização';

                setFieldErrors(fieldErrors);
                toast(message, {
                    toastId: 'org-error',
                    position: 'top-center',
                    type: 'error',
                    autoClose: 5000,
                });
            },
        }
    );

    // Função para buscar dados do CNPJ na API do backend
    const buscarDadosCNPJ = useCallback(async (cnpj: string) => {
        if (!cnpj || cnpj.replace(/\D/g, '').length !== 14) {
            return;
        }

        setIsBuscandoCNPJ(true);
        try {
            const cnpjLimpo = cnpj.replace(/\D/g, '');
            const response = await axios.get(`/api/organizacao/busca/cnpj/${cnpjLimpo}`, {
                withCredentials: true
            });

            if (response.data && response.data.ok) {
                const dados = response.data.result;
                setOrganizacao(prev => ({
                    ...prev,
                    razao_social: dados.razao_social || prev.razao_social,
                    nome_fantasia: dados.nome_fantasia || prev.nome_fantasia,
                    email: dados.email || prev.email,
                    endereco_cep: dados.endereco_cep || prev.endereco_cep,
                    endereco_logradouro: dados.endereco_logradouro || prev.endereco_logradouro,
                    endereco_numero: dados.endereco_numero || prev.endereco_numero,
                    endereco_bairro: dados.endereco_bairro || prev.endereco_bairro,
                    endereco_cidade: dados.endereco_cidade || prev.endereco_cidade,
                    endereco_estado: dados.endereco_estado || prev.endereco_estado,
                    endereco_pais: dados.endereco_pais || prev.endereco_pais,
                    endereco_complemento: dados.endereco_complemento || prev.endereco_complemento,
                    endereco_cidade_id_ibge: dados.endereco_cidade_id_ibge || prev.endereco_cidade_id_ibge,
                }));
                toast('Dados da empresa preenchidos automaticamente!', {
                    toastId: 'auto-cnpj-success',
                    position: 'top-center',
                    type: 'success',
                    autoClose: 5000,
                });
            } else {
                toast(response.data?.msg || 'CNPJ não encontrado ou inválido', {
                    toastId: 'auto-cnpj-error',
                    position: 'top-center',
                    type: 'error',
                    autoClose: 5000,
                });
            }
        } catch (error: any) {
            console.error('Erro ao buscar dados do CNPJ:', error);
            const message = error.response?.data?.msg || 'Erro ao buscar dados do CNPJ. Verifique se o CNPJ está correto.';
            toast(message, {
                toastId: 'auto-cnpj-error',
                position: 'top-center',
                type: 'error',
                autoClose: 5000,
            });
        } finally {
            setIsBuscandoCNPJ(false);
        }
    }, []);

    // Função para buscar dados do CEP
    const buscarDadosCEP = useCallback(async (cep: string) => {
        if (!cep || cep.replace(/\D/g, '').length !== 8) {
            return;
        }

        setIsBuscandoCEP(true);
        try {
            const cepLimpo = cep.replace(/\D/g, '');
            const response = await axios.get(`/api/organizacao/busca/cep/${cepLimpo}`, {
                withCredentials: true
            });

            if (response.data && response.data.ok) {
                const dados = response.data.result;
                setOrganizacao(prev => ({
                    ...prev,
                    endereco_logradouro: dados.endereco_logradouro || prev.endereco_logradouro,
                    endereco_bairro: dados.endereco_bairro || prev.endereco_bairro,
                    endereco_cidade: dados.endereco_cidade || prev.endereco_cidade,
                    endereco_estado: dados.endereco_estado || prev.endereco_estado,
                    endereco_pais: dados.endereco_pais || prev.endereco_pais,
                    endereco_cidade_id_ibge: dados.endereco_cidade_id_ibge || prev.endereco_cidade_id_ibge,
                }));
                toast('Endereço preenchido automaticamente!', {
                    toastId: 'auto-cep-success',
                    position: 'top-center',
                    type: 'success',
                    autoClose: 5000,
                });
            } else {
                toast(response.data?.msg || 'CEP não encontrado', {
                    toastId: 'auto-cep-error',
                    position: 'top-center',
                    type: 'error',
                    autoClose: 5000,
                });
            }
        } catch (error: any) {
            console.error('Erro ao buscar dados do CEP:', error);
            const message = error.response?.data?.msg || 'Erro ao buscar dados do CEP. Verifique se o CEP está correto.';
            toast(message, {
                toastId: 'auto-cep-error',
                position: 'top-center',
                type: 'error',
                autoClose: 5000,
            });
        } finally {
            setIsBuscandoCEP(false);
        }
    }, []);

    const handleSave = () => {
        saveMutation.mutate(organizacao);
    };

    const handleChange = (field: string, value: string) => {
        setOrganizacao((prev: OrganizacaoDto) => ({ ...prev, [field]: value }));
        // Limpar erro do campo quando usuário começar a digitar
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: '' }));
        }

        // Buscar dados automaticamente quando CNPJ for preenchido
        if (field === 'cnpj' && value.replace(/\D/g, '').length === 14) {
            buscarDadosCNPJ(value);
        }

        // Buscar dados automaticamente quando CEP for preenchido
        if (field === 'endereco_cep' && value.replace(/\D/g, '').length === 8) {
            buscarDadosCEP(value);
        }
    };

    if (isLoading) {
        return (
            <VBox center style={{ padding: '40px' }}>
                <Botao variant="outline-secondary" icon={mdiLoading} disabled>
                    Carregando...
                </Botao>
            </VBox>
        );
    }

    const disableFields = saveMutation.isLoading || isBuscandoCNPJ || isBuscandoCEP;

    return (
        <VBox gap="24px">
            <HBox>
                <VBox gap="8px">
                    <h3>Organização</h3>
                    <p style={{ color: 'var(--tc-color-gray-600)' }}>
                        Configure os dados cadastrais da sua organização. Estas informações
                        são utilizadas para emissão de documentos fiscais e identificação da empresa.
                    </p>
                </VBox>
                <Botao
                    variant="primary"
                    icon={saveMutation.isLoading ? mdiLoading : mdiContentSave}
                    onClick={handleSave}
                    disabled={saveMutation.isLoading || disableFields}
                >
                    {saveMutation.isLoading ? 'Salvando...' : 'Salvar'}
                </Botao>
            </HBox>

            <Grid>
                <GridItem sm={12}><h3>Dados gerais</h3></GridItem>
                <GridItem sm={12}>
                    <SecaoLogo />
                </GridItem>
                <GridItem sm={12}>
                    <CampoMemo
                        label="Descrição da Empresa"
                        value={organizacao.descricao_curta || ''}
                        onChange={(v) => handleChange('descricao_curta', v)}
                        placeholder="Descreva o ramo de atividade e detalhes da empresa..."
                        error={fieldErrors.descricao_curta}
                        rows={4}
                        disabled={disableFields}
                    />
                    <small style={{ color: 'var(--tc-color-gray-600)', marginTop: '4px', display: 'block' }}>
                        Esta descrição é utilizada pelos agentes de IA para compreender melhor o contexto da empresa.
                    </small>
                </GridItem>

                <GridItem sm={12} md={6}>
                    <CampoSeletor
                        label="Fuso horário (timezone)"
                        value={organizacao.timezone || null}
                        onChange={(v) => handleChange('timezone', v || '')}
                        options={TIMEZONE_OPTIONS}
                        placeholder="Selecione o fuso horário..."
                        error={fieldErrors.timezone}
                        disabled={disableFields}
                        nullable
                    />
                    <small style={{ color: 'var(--tc-color-gray-600)', marginTop: '4px', display: 'block' }}>
                        Usado para calcular o horário de retornos agendados no fluxo. Se não definido, utiliza o horário do servidor.
                    </small>
                </GridItem>

                <GridItem sm={12}><h3>Contato</h3></GridItem>

                <GridItem sm={12}>
                    <CampoMultiTexto
                        label="E-mail(s) Financeiro"
                        value={organizacao.email?.split(',')?.map(s => s.trim()) || []}
                        onChange={(v) => handleChange('email', v?.join(', '))}
                        placeholder="financeiro@empresa.com"
                        error={fieldErrors.email}
                        type="email"
                        disabled={disableFields}
                    />
                    <small>Este e-mail será utilizado para comunicação financeira (envio de faturas, notificações de pagamento, etc.)</small>
                </GridItem>

                <GridItem sm={12}>
                    <CampoMultiTexto
                        label="E-mail(s) Administrativo"
                        value={organizacao.email_administrativo?.split(',')?.map(s => s.trim()) || []}
                        onChange={(v) => handleChange('email_administrativo', v?.join(', '))}
                        placeholder="admin@empresa.com"
                        error={fieldErrors.email_administrativo}
                        type="email"
                        disabled={disableFields}
                    />
                    <small>Este e-mail será utilizado para comunicações administrativas e gerenciais</small>
                </GridItem>

                <GridItem sm={12}><h3>Endereço</h3></GridItem>

                <GridItem sm={12} md={4}>
                    <VBox gap="4px">
                        <HBox gap="8px" style={{ alignItems: 'flex-end' }}>
                            <div style={{ flex: 1 }}>
                                <CampoMascara
                                    label="CNPJ"
                                    value={organizacao.cnpj || ''}
                                    onChange={(v) => handleChange('cnpj', v)}
                                    masks={["99.999.999/9999-99"]}
                                    error={fieldErrors.cnpj}
                                    disabled={disableFields}
                                />
                            </div>
                            <Botao
                                variant="outline-primary"
                                icon={isBuscandoCNPJ ? mdiLoading : mdiMagnify}
                                onClick={() => buscarDadosCNPJ(organizacao.cnpj || '')}
                                disabled={isBuscandoCNPJ || saveMutation.isLoading || !organizacao.cnpj || organizacao.cnpj.replace(/\D/g, '').length !== 14}
                                style={{ marginBottom: fieldErrors.cnpj ? '24px' : '0' }}
                            >
                                {isBuscandoCNPJ ? 'Buscando...' : 'Buscar'}
                            </Botao>
                        </HBox>
                        <small>Este CNPJ será utilizado para emissão de documentos fiscais.</small>
                        <small>Use a busca para preencher os dados da empresa com as informações do CNPJ.</small>
                    </VBox>
                </GridItem>

                <GridItem sm={12} md={4}>
                    <CampoTexto
                        label="Razão Social"
                        value={organizacao.razao_social || ''}
                        onChange={(v) => handleChange('razao_social', v)}
                        placeholder="Razão social completa"
                        error={fieldErrors.razao_social}
                        disabled={true}
                    />
                </GridItem>

                <GridItem sm={12} md={4}>
                    <CampoTexto
                        label="Nome Fantasia"
                        value={organizacao.nome_fantasia || ''}
                        onChange={(v) => handleChange('nome_fantasia', v)}
                        placeholder="Nome da empresa"
                        error={fieldErrors.nome_fantasia}
                        required
                        disabled={disableFields}
                    />
                </GridItem>


                <GridItem sm={12} md={3}>
                    <VBox gap="4px">
                        <HBox gap="8px" style={{ alignItems: 'flex-end' }}>
                            <div style={{ flex: 1 }}>
                                <CampoMascara
                                    label="CEP"
                                    value={organizacao.endereco_cep || ''}
                                    onChange={(v) => handleChange('endereco_cep', v)}
                                    masks={["99999-999"]}
                                    error={fieldErrors.endereco_cep}
                                    disabled={true}
                                />
                            </div>
                            <Botao
                                variant="outline-secondary"
                                icon={isBuscandoCEP ? mdiLoading : mdiMapMarker}
                                onClick={() => buscarDadosCEP(organizacao.endereco_cep || '')}
                                disabled={true || isBuscandoCEP || saveMutation.isLoading || !organizacao.endereco_cep || organizacao.endereco_cep?.replace(/\D/g, '').length !== 8}
                                style={{ marginBottom: fieldErrors.endereco_cep ? '24px' : '0' }}
                                size="sm"
                            >
                                {isBuscandoCEP ? 'Buscando...' : 'Buscar'}
                            </Botao>
                        </HBox>
                        <small>Preenche automaticamente o endereço</small>
                    </VBox>
                </GridItem>

                <GridItem sm={12} md={7}>
                    <CampoTexto
                        label="Logradouro/Endereço"
                        value={organizacao.endereco_logradouro || ''}
                        onChange={(v) => handleChange('endereco_logradouro', v)}
                        placeholder="Rua Exemplo, 123"
                        error={fieldErrors.endereco_logradouro}
                        disabled={true}
                    />
                </GridItem>
                <GridItem sm={12} md={2}>
                    <CampoTexto
                        label="Número"
                        value={organizacao.endereco_numero || ''}
                        onChange={(v) => handleChange('endereco_numero', v)}
                        placeholder="123"
                        error={fieldErrors.endereco_numero}
                        disabled={true}
                    />
                </GridItem>

                <GridItem sm={12} md={3}>
                    <CampoTexto
                        label="Bairro"
                        value={organizacao.endereco_bairro || ''}
                        onChange={(v) => handleChange('endereco_bairro', v)}
                        placeholder="Bairro Exemplo"
                        error={fieldErrors.endereco_bairro}
                        disabled={true}
                    />
                </GridItem>

                <GridItem sm={12} md={2}>
                    <CampoTexto
                        label="Complemento"
                        value={organizacao.endereco_complemento || ''}
                        onChange={(v) => handleChange('endereco_complemento', v)}
                        placeholder="Apto. 101"
                        error={fieldErrors.endereco_complemento}
                        disabled={true}
                    />
                </GridItem>

                <GridItem sm={12} md={3}>
                    <CampoTexto
                        label="Cidade"
                        value={organizacao.endereco_cidade || ''}
                        onChange={(v) => handleChange('endereco_cidade', v)}
                        placeholder="Cidade Exemplo"
                        error={fieldErrors.endereco_cidade}
                        disabled={true}
                    />
                    <small>Código IBGE: {organizacao.endereco_cidade_id_ibge || ''}</small>
                </GridItem>
                <GridItem sm={12} md={2}>
                    <CampoTexto
                        label="Estado"
                        value={organizacao.endereco_estado || ''}
                        onChange={(v) => handleChange('endereco_estado', v)}
                        placeholder="Estado Exemplo"
                        error={fieldErrors.endereco_estado}
                        disabled={true}
                    />
                </GridItem>
                <GridItem sm={12} md={2}>
                    <CampoTexto
                        label="País"
                        value={organizacao.endereco_pais || ''}
                        onChange={(v) => handleChange('endereco_pais', v)}
                        placeholder="BR"
                        error={fieldErrors.endereco_pais}
                        disabled={true}
                    />
                </GridItem>
            </Grid>

            <HBox gap="12px" style={{ justifyContent: 'flex-end' }}>
                <Botao
                    variant="primary"
                    icon={saveMutation.isLoading ? mdiLoading : mdiContentSave}
                    onClick={handleSave}
                    disabled={saveMutation.isLoading || disableFields}
                >
                    {saveMutation.isLoading ? 'Salvando...' : 'Salvar'}
                </Botao>
            </HBox>
        </VBox>
    );
};

export default SecaoOrganizacao;